import React from 'react';
import {View} from 'react-native';
import Animated, {FadeIn, FadeOut, useReducedMotion} from 'react-native-reanimated';

import type {SkeletonGroupProps} from '../types';

/**
 * Coordinates skeleton display and cross-fade transition.
 *
 * When isLoading=true: renders skeleton template with accessibility busy state.
 * When isLoading=false: renders children (real content).
 * Transition: FadeOut skeleton (250ms) → FadeIn content (300ms).
 */
export const SkeletonGroup: React.FC<SkeletonGroupProps> = ({
  isLoading,
  crossFade = true,
  children,
  skeleton,
}) => {
  const reducedMotion = useReducedMotion();
  const shouldAnimate = crossFade && !reducedMotion;

  if (isLoading) {
    const skeletonContent = (
      <View
        accessibilityRole="progressbar"
        accessibilityLabel="Loading content"
        accessibilityState={{busy: true}}>
        {skeleton}
      </View>
    );

    return shouldAnimate ? (
      <Animated.View exiting={FadeOut.duration(250)}>{skeletonContent}</Animated.View>
    ) : (
      skeletonContent
    );
  }

  return shouldAnimate ? (
    <Animated.View entering={FadeIn.duration(300)}>{children}</Animated.View>
  ) : (
    <>{children}</>
  );
};
